import {
  isLoanStatus,
  type LoanStatus,
} from '../enums/loan-status.enum.js'
import { LOAN_HAPPY_PATH } from '../loans/loan-status-machine.js'

/**
 * Evaluation / enforcement moments.
 * Status gates use `STATUS:<LoanStatus>` for transition **into** that status.
 */
export const UNDERWRITING_GATES = [
  'ONGOING',
  'PRE_APPROVAL',
  'STATUS:PRE_APPROVED',
  'STATUS:APPROVED',
  'STATUS:CLEAR_TO_CLOSE',
  'STATUS:CLEAR_TO_CLOSE_FUND',
  'STATUS:ORIGINATED',
] as const

export type UnderwritingGate = (typeof UNDERWRITING_GATES)[number]

export const UNDERWRITING_SEVERITIES = ['WARN', 'BLOCK'] as const
export type UnderwritingSeverity = (typeof UNDERWRITING_SEVERITIES)[number]

/** Happy-path statuses from PRE_APPROVED through ORIGINATED (inclusive). */
export const HAPPY_PATH_FROM_PRE_APPROVED: readonly LoanStatus[] =
  LOAN_HAPPY_PATH.slice(LOAN_HAPPY_PATH.indexOf('PRE_APPROVED'))

/** Happy-path statuses from CLEAR_TO_CLOSE through ORIGINATED (inclusive). */
export const HAPPY_PATH_FROM_CLEAR_TO_CLOSE: readonly LoanStatus[] =
  LOAN_HAPPY_PATH.slice(LOAN_HAPPY_PATH.indexOf('CLEAR_TO_CLOSE'))

const GATE_SET: ReadonlySet<string> = new Set(UNDERWRITING_GATES)

export function isUnderwritingGate(value: string): value is UnderwritingGate {
  return GATE_SET.has(value)
}

/**
 * Gate for transition **into** `status`, or null when guidelines do not
 * apply to that target (exception exits, early statuses without STATUS gates).
 */
export function tryStatusGate(status: LoanStatus): UnderwritingGate | null {
  const candidate = `STATUS:${status}`
  return isUnderwritingGate(candidate) ? candidate : null
}

/**
 * Gate for transition into `status`. Prefer {@link tryStatusGate} when the
 * target may be an exception exit.
 * @throws if `status` has no STATUS underwriting gate
 */
export function statusGate(status: LoanStatus): UnderwritingGate {
  const gate = tryStatusGate(status)
  if (gate == null) {
    throw new Error(`No underwriting status gate for loan status ${status}`)
  }
  return gate
}

export function parseStatusGate(
  gate: UnderwritingGate,
): LoanStatus | null {
  if (!gate.startsWith('STATUS:')) return null
  const raw = gate.slice('STATUS:'.length)
  return isLoanStatus(raw) ? raw : null
}

/** True if `to` is on the happy path and at or after `from` (inclusive). */
export function isHappyPathStatusAtOrAfter(
  to: LoanStatus,
  from: LoanStatus,
): boolean {
  const fromIdx = LOAN_HAPPY_PATH.indexOf(from)
  const toIdx = LOAN_HAPPY_PATH.indexOf(to)
  if (fromIdx < 0 || toIdx < 0) return false
  return toIdx >= fromIdx
}
